import type {
  UserPreferencesUpdateData,
  UserProfileUpdateData,
} from '@mogul/api-client';
import { ErrorCode } from '@mogul/api-client';
import { convertFileToBase64, parseApiErrors } from '@mogul/shared';
import React, { useContext, useState } from 'react';
import toast from 'react-hot-toast';

import { emailApi, viewerApi, viewerApiFactory } from '../../../api';
import { useAuth } from '../../../auth';
import { Enable2FAModal } from '../../../components/common/modal/Enable2FAModal.component';
import { PasswordChangeModal } from '../../../components/common/modal/PasswordChangeModal.component';
import { NotificationsComponent } from '../../../components/profile/Notifications.component';
import { ProfileComponent } from '../../../components/profile/Profile.component';
import { ModalStateContext } from '../../../contexts/app-state-context/modal-state.context';

export const ProfilePage = () => {
  const {
    user,
    updateProfile,
    verifyMfa,
    initMfa,
    disableMfa,
    getToggleEmailNotify,
  } = useAuth();

  const [loading, setLoading] = useState(false);

  const handleImageUpload = async (file: File) => {
    const { size, type, name } = file;

    setLoading(true);
    const base64 = await convertFileToBase64(file);
    await viewerApi.setProfileImage({
      fileCreateData: {
        size: String(size),
        mimeType: type,
        filename: name,
        encoding: null,
        base64,
      },
    });
    setLoading(false);
  };

  const { setModal } = useContext(ModalStateContext);

  const handleUpdateProfile = async (data: UserProfileUpdateData) => {
    setLoading(true);
    try {
      await updateProfile(data);
      toast.success('Changes saved');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailNotify = async (data: UserPreferencesUpdateData) => {
    try {
      await getToggleEmailNotify(data);
      if (data.dropNotifyConsent ?? false) {
        await emailApi.addNotify();
      } else {
        await emailApi.removeNotify();
      }

      toast.success('Changes saved');
    } finally {
      setLoading(false);
    }
  };

  // change password modal functions
  const handleChangePassword = async (input: {
    oldPassword: string;
    newPassword: string;
  }) => {
    setLoading(true);

    try {
      await viewerApiFactory({
        username: user?.username,
        password: input.oldPassword,
      }).setViewerPassword({
        setPasswordData: {
          password: input.newPassword,
        },
      });

      toast.success('Password changed');
    } finally {
      setLoading(false);
    }
  };

  const handleClickChangePassword = () => {
    setModal(PasswordChangeModal(handleChangePassword));
  };

  const handleVerifyMfa = (otpCode: string) =>
    verifyMfa({ otpCode }).catch(error => {
      for (const { code, detail } of parseApiErrors(error)) {
        if (code === ErrorCode.AuthUnauthorized) {
          toast.error(detail ?? 'Invalid code');
          return;
        }
      }
    });

  const handleInitMfa = async () => {
    setLoading(true);
    let authUrl;

    try {
      ({ authUrl } = await initMfa());
    } catch (error) {
      for (const { code } of parseApiErrors(error)) {
        if (code === ErrorCode.UserMfaActive) {
          toast.error('Error Initializing Two Factor Auth');
          return;
        }
      }

      throw error;
    } finally {
      setLoading(false);
    }

    setModal(Enable2FAModal(authUrl, handleVerifyMfa));
  };

  const handleDisableMfa = () => {
    return disableMfa();
  };

  return (
    <div className="flex flex-col items-center h-full bg-container bg-cover bg-repeat pb-24">
      <div className={`mt-10 mb-20 md:mx-8 mx-4 md:w-1/2 xl:w-1/3`}>
        <ProfileComponent
          loading={loading}
          name={user?.name ?? ''}
          profileImageSrc={user?.image?.publicUrl}
          onUploadImage={handleImageUpload}
          onClickChangePassword={handleClickChangePassword}
          mfaActive={user?.mfaActive === true}
          onInitMfa={handleInitMfa}
          onDisableMfa={handleDisableMfa}
          onUpdateProfile={handleUpdateProfile}
        />
        <NotificationsComponent
          loading={loading}
          handleEmailNotify={handleEmailNotify}
          onUpdateProfile={handleUpdateProfile}
          //notifyState={user?.preferences?.dropNotifyConsent ?? false}
          preferences={user?.preferences ?? {}}
        />
      </div>
    </div>
  );
};
